import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useStore from "../store";
import { ISecurity } from "./Loan";
import { Button } from "../components/form";
import { Pencil, Trash } from "../icons";
import { Alert } from "../components/alert";
import { formatter } from "../util";
import { SecurityFormModal, propertyTypeOptionsData, usageTypeData } from ".";
import { currencyAbbreviation } from "../util/formater";
import { SectionLabel } from "../components/label";
import classNames from "classnames";

type Props = {
  SecurityId: ISecurity["SecurityId"];
};

export default function Security({ SecurityId }: Props) {
  const [deleting, setDeleting] = useState(false);
  const [isEditing, setEditing] = useState(false);

  const [
    ApplicantParty,
    Security,
    Loans,
    LoanSecurityMapping,
    stateLoans,
    addLoan,
    setLoanItem,
    setSecurityItem,
    setFinancialItem,
    addFinancial,
    addLoanSecurityMapping,
    deleteLoanItem,
    deleteSecurityItem,
    deleteMappingItem,
    deleteFinancialItem,
    addEventListener,
  ] = useStore(
    useCallback(
      (state) => [
        Array.from(state.ApplicantParties.values())[0],
        state.Security.get(SecurityId),
        Array.from(state.Loans.values()),
        Array.from(state.LoanSecurityMapping.values()).filter(
          (_map) => _map.SecurityId === SecurityId,
        ),
        state.Loans,
        state.addLoan,
        state.setLoanItem,
        state.setSecurityItem,
        state.setFinancialItem,
        state.addFinancial,
        state.addLoanSecurityMapping,
        state.deleteLoanItem,
        state.deleteSecurityItem,
        state.deleteMappingItem,
        state.deleteFinancialItem,
        state.addEventListener,
      ],
      [SecurityId],
    ),
  );

  const securityFormInitialValue = useRef<
    ComponentProps<typeof SecurityFormModal>["initialValue"]
  >({
    Security,
  });

  const LoanList = useMemo(() => {
    if (!Security) return [];

    const _list = (Loans || [])
      .filter((_loan) => {
        return (
          !!(LoanSecurityMapping || []).find(
            (_i) =>
              _i.LoanId === _loan.LoanId &&
              _i.SecurityId === Security?.SecurityId,
          )?.LoanId || false
        );
      })
      .map((_loan) => {
        const Liability = Array.from(ApplicantParty.Liability.values()).find(
          (liability) => liability.LoanId === _loan.LoanId,
        ) || {
          LiabilityType: "HomeLoan",
          RepaymentAmount: NaN,
          RepaymentFrequency: "Monthly",
          Limit: null,
          Balance: null,
          InterestRate: null,
          RepaymentType: null,
          StartDate: null,
          TotalTermYears: NaN,
          TotalTermMonths: NaN,
          InterestOnlyTermYears: NaN,
          InterestOnlyTermMonths: NaN,
          Lender: null,
          IsRevolvingCredit: null,
        };
        return {
          ..._loan,
          Liability,
        };
      });

    securityFormInitialValue.current = {
      Security,
      Loans: _list,
    };

    return _list;
  }, [ApplicantParty.Liability, Security, Loans, LoanSecurityMapping]);

  function showModal() {
    setEditing(true);
  }
  function closeModal() {
    setEditing(false);
  }

  function deleteLoan() {
    deleteSecurityItem(SecurityId);
    (LoanList || []).forEach((Loan) => {
      deleteLoanItem(Loan.LoanId);
      const _Loan = securityFormInitialValue.current?.Loans?.find(
        (_loan) => _loan.LoanId === Loan.LoanId,
      );
      if (_Loan && _Loan.Liability && _Loan.Liability?._id) {
        deleteFinancialItem(ApplicantParty._id, {
          type: "Liability",
          id: _Loan.Liability?._id,
        });
      }
    });
    (LoanSecurityMapping || []).forEach((Map) => {
      deleteMappingItem(Map._id);
    });
    setDeleting(false);
  }

  const updateLoan: ComponentProps<typeof SecurityFormModal>["onSave"] = (
    value,
  ) => {
    const { Loans: NewLoans, Security: NewSecurity } = value;
    if (!NewLoans || !NewSecurity) return;

    setSecurityItem(NewSecurity.SecurityId, NewSecurity);

    (NewLoans || []).forEach((Loan) => {
      const existingLoan = stateLoans.get(Loan.LoanId);

      if (existingLoan) {
        setLoanItem(existingLoan._id, Loan);

        const previousLoan = securityFormInitialValue.current?.Loans?.find(
          (_loan) => _loan.LoanId === Loan.LoanId,
        );

        switch (previousLoan?.LoanType) {
          case "New":
            if (Loan.LoanType === "Existing") {
              addFinancial(ApplicantParty._id, {
                type: "Liability",
                value: {
                  LoanId: Loan.LoanId,
                  Lender: Loan.Liability?.Lender ?? null,
                  Balance: Loan.Liability?.Balance ?? null,
                  Limit: Loan.Liability?.Limit ?? null,
                  InterestRate: Loan.Liability?.InterestRate ?? null,
                  RepaymentAmount: Loan.Liability?.RepaymentAmount ?? null,
                  RepaymentFrequency:
                    Loan.Liability?.RepaymentFrequency ?? null,
                  RepaymentType: Loan.Liability?.RepaymentType ?? null,
                  TotalTermMonths: Loan.Liability?.TotalTermMonths ?? null,
                  StartDate: Loan.Liability?.StartDate ?? null,
                  IsRevolvingCredit: Loan.Liability?.IsRevolvingCredit ?? null,
                  InterestOnlyTermMonths:
                    Loan.Liability?.InterestOnlyTermMonths ?? null,
                  ApplicantId: Array.from(ApplicantParty.Applicant.values())[0]
                    .ApplicantId,
                  LiabilityType: "HomeLoan",
                },
              });
            }
            break;
          case "Existing":
            if (previousLoan.Liability) {
              if (
                Loan.LoanType === "New" &&
                previousLoan.Liability &&
                previousLoan.Liability?._id
              ) {
                deleteFinancialItem(ApplicantParty._id, {
                  id: previousLoan.Liability?._id,
                  type: "Liability",
                });
              } else if (
                Loan.LoanType === "Existing" &&
                Loan.Liability &&
                Loan.Liability?._id
              ) {
                setFinancialItem(ApplicantParty._id, {
                  id: Loan.Liability._id,
                  type: "Liability",
                  payload: Loan.Liability,
                });
              }
            }
            break;
          default:
            throw Error("Unknown Loan Type");
        }
      } else {
        const _newLoan = addLoan(Loan);

        if (_newLoan && Security) {
          addLoanSecurityMapping({
            LoanId: _newLoan.LoanId,
            SecurityId: Security?.SecurityId,
          });
        }

        if (
          _newLoan &&
          _newLoan.LoanType === "Existing" &&
          _newLoan.LendingAction !== null &&
          Loan?.Liability
        ) {
          addFinancial(ApplicantParty._id, {
            type: "Liability",
            value: {
              Lender: Loan?.Liability?.Lender ?? null,
              Balance: Loan?.Liability?.Balance ?? null,
              Limit: Loan?.Liability?.Limit ?? null,
              InterestRate: Loan?.Liability?.InterestRate ?? null,
              RepaymentAmount: Loan?.Liability?.RepaymentAmount ?? null,
              RepaymentFrequency: Loan?.Liability?.RepaymentFrequency ?? null,
              RepaymentType: Loan?.Liability?.RepaymentType ?? null,
              TotalTermMonths: Loan?.Liability?.TotalTermMonths ?? null,
              StartDate: Loan?.Liability?.StartDate ?? null,
              IsRevolvingCredit: Loan?.Liability?.IsRevolvingCredit ?? null,
              InterestOnlyTermMonths:
                Loan?.Liability?.InterestOnlyTermMonths ?? null,
              ApplicantId: Array.from(ApplicantParty.Applicant.values())[0]
                .ApplicantId,
              LiabilityType: "HomeLoan",
              LoanId: _newLoan.LoanId,
            },
          });
        }
      }
    });

    securityFormInitialValue.current = {
      Security: NewSecurity,
      Loans: NewLoans,
    };
    closeModal();
  };

  useEffect(() => {
    if (!SecurityId) return;

    addEventListener("starteditfinancial", (e) => {
      if (
        e.type === "Liability" &&
        e.securityId &&
        e.securityId === SecurityId
      ) {
        showModal();
      }
    });
  }, [SecurityId, addEventListener]);

  if (!Security) return null;

  const PropertyType = propertyTypeOptionsData.find(
    (propertyType) => propertyType.code === Security.PropertyType[0],
  );
  const UsageType = usageTypeData.find(
    (usageType) => usageType.code === Security.UsageType,
  );

  const TotalLoanAmount = LoanList.reduce(
    (acc, loan) =>
      acc +
      (loan.LoanType === "Existing" &&
      loan.LendingAction === "None" &&
      (loan.Liability.IsRevolvingCredit === false ||
        loan.Liability.IsRevolvingCredit === null)
        ? loan?.Liability?.Balance || 0
        : loan.LoanType === "Existing" && loan.LendingAction === "TopUp"
        ? (!isNaN(loan.LoanAmount)
            ? loan.LoanAmount
            : loan?.Liability?.Limit || 0) +
          (!isNaN(loan.TopUpAmount) ? loan.TopUpAmount : 0)
        : loan.LoanType === "New"
        ? loan.LoanAmount
        : !isNaN(loan.LoanAmount)
        ? loan.LoanAmount
        : loan?.Liability?.Limit || 0),
    0,
  );
  const DepositAmount = Security.Value - TotalLoanAmount;
  const hasNegativeDeposit = DepositAmount < 0;
  let LVR = (TotalLoanAmount / Security.Value) * 100;
  if (LVR % 1 !== 0) {
    LVR = parseFloat(LVR.toFixed(2));
  }

  return (
    <>
      <SecurityFormModal
        title="Edit security"
        mode="edit"
        open={isEditing}
        SecurityId={SecurityId}
        initialValue={{
          ...securityFormInitialValue.current,
        }}
        handleClose={closeModal}
        onDelete={deleteLoan}
        onSave={updateLoan}
      />
      <section className="rounded-[3px] bg-[#F7F4F9] p-[10px]">
        <header className="flex items-center">
          <ul className="flex items-center justify-start gap-1">
            <SectionLabel className="desktop:text-sm">
              <span className="block leading-[1.2em]">
                SECURITY {Security?.SecurityId}
              </span>
            </SectionLabel>
          </ul>
          <Button
            className="ml-auto w-max bg-transparent p-2 text-accent underline"
            size="small"
            onClick={showModal}
          >
            <Pencil
              fill="#9B88AA"
              width={undefined}
              height={undefined}
              className="w-[10px] desktop:w-[14px]"
            />
          </Button>
          <Alert
            below={
              <Button
                className="-mr-2 w-max bg-transparent p-2 text-accent underline"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleting(true);
                }}
              >
                <Trash className="w-[10px] desktop:w-[14px]" />
              </Button>
            }
            open={deleting}
            backgroundColor="#1E1642"
            className="translate-y-1.5 text-left font-helvetica text-[11px] text-white shadow-none desktop:text-sm"
            arrowClassName="-translate-y-1 -translate-x-1"
            onClose={() => setDeleting(false)}
          >
            <p className="font-bold">Are you sure?</p>
            <p className="mt-[5px]">Deleting this item cannot be undone.</p>
            <div className="mt-3 flex items-center justify-end space-x-[28px]">
              <Button
                size="small"
                className="w-max bg-transparent text-white"
                onClick={() => setDeleting(false)}
              >
                <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                  Cancel
                </span>
              </Button>
              <Button
                className="w-max bg-[#E13756] px-4 py-2 font-bold text-white"
                size="small"
                onClick={deleteLoan}
              >
                <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                  Yes, delete
                </span>
              </Button>
            </div>
          </Alert>
        </header>
        <section className="font-helvetica text-[15px] leading-[18px] text-[#83748E]">
          <p className="mb-1 flex gap-2">
            <span>{formatter.currencyAbbreviation(Security.Value)}</span>
            {PropertyType && (
              <>
                {" "}
                <i>•</i> <span>{PropertyType.type}</span>
              </>
            )}
          </p>
          {UsageType && <span>{UsageType.name}</span>}
        </section>
        {LoanList.map((Loan) => {
          const LoanAmount =
            Loan.LoanType === "Existing" &&
            Loan.LendingAction === "None" &&
            (Loan.Liability.IsRevolvingCredit === false ||
              Loan.Liability.IsRevolvingCredit === null)
              ? Loan?.Liability?.Balance
              : Loan.LoanType === "New" || Loan.LendingAction === "Refinance"
              ? Loan.LoanAmount
              : Loan?.Liability?.Limit;
          const TotalTermMonths =
            ((Loan.LoanType === "Existing" && Loan.LendingAction === "None"
              ? Loan?.Liability?.TotalTermMonths
              : Loan.TotalTermMonths) || 0) % 12;
          const TotalTermYears = Math.floor(
            ((Loan.LoanType === "Existing" && Loan.LendingAction === "None"
              ? Loan?.Liability?.TotalTermMonths
              : Loan.TotalTermMonths) || 0) / 12,
          );
          const Term = `${TotalTermYears}${
            TotalTermMonths ? "y" : TotalTermYears > 1 ? " years" : " year"
          } ${TotalTermMonths ? `${TotalTermMonths}m` : ""}`;
          return (
            <section
              className="mt-3 flex items-end gap-2 rounded-md border border-[#CDC4D5] px-2.5 py-1.5 pl-2 font-helvetica text-[13px] text-[#83748E]"
              key={Loan.LoanId}
            >
              <div
                className={classNames(
                  "min-h-[43px] w-[5px] rounded",
                  Loan.LoanType === "New"
                    ? "bg-[#42B4B4]"
                    : Loan.LendingAction === "Refinance"
                    ? "bg-[#E29749]"
                    : Loan.LendingAction === "TopUp"
                    ? "bg-[#577CF7]"
                    : "bg-[#9B88AA]",
                )}
              ></div>
              <div className="grid gap-1">
                <p className="text-[11px] font-bold">
                  {Loan.LoanType === "New"
                    ? "NEW"
                    : Loan.LendingAction === "Refinance"
                    ? "REFINANCE"
                    : Loan.LendingAction === "TopUp"
                    ? "TOP-UP"
                    : "EXISTING"}{" "}
                  LOAN
                </p>
                <p className="flex gap-1 text-[14px] font-semibold">
                  {currencyAbbreviation(LoanAmount || 0)}
                  {Loan.LoanType === "Existing" &&
                    Loan.LendingAction === "None" && (
                      <span className="font-normal">
                        {Loan?.Liability?.IsRevolvingCredit
                          ? "Limit"
                          : "Balance"}
                      </span>
                    )}
                </p>
              </div>
              <div className="flex flex-1 items-center justify-center">
                {Loan.LendingAction === "TopUp" && Loan.TopUpAmount && (
                  <span className="flex items-center gap-1 rounded-full bg-[#577CF7] py-[3px] pl-[7px] pr-[8px] text-[13px] font-semibold leading-[1] text-white">
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 8 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.23926 2.99707V4.60352H0.416992V2.99707H7.23926ZM4.69629 0.255859V7.50195H2.9668V0.255859H4.69629Z"
                        fill="white"
                      />
                    </svg>
                    {currencyAbbreviation(Loan.TopUpAmount)}
                  </span>
                )}
              </div>
              <div className="text-[14px flex h-fit items-center gap-1.5">
                <svg
                  width="14"
                  height="13"
                  viewBox="0 0 14 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7 0C5.71442 0 4.45772 0.381218 3.3888 1.09545C2.31988 1.80968 1.48676 2.82484 0.994786 4.01256C0.502816 5.20028 0.374095 6.50721 0.624899 7.76809C0.875703 9.02896 1.49477 10.1872 2.40381 11.0962C3.31285 12.0052 4.47104 12.6243 5.73192 12.8751C6.99279 13.1259 8.29973 12.9972 9.48744 12.5052C10.6752 12.0132 11.6903 11.1801 12.4046 10.1112C13.1188 9.04229 13.5 7.78558 13.5 6.5C13.4982 4.77665 12.8128 3.12441 11.5942 1.90582C10.3756 0.687224 8.72335 0.00181989 7 0ZM7 12C5.91221 12 4.84884 11.6774 3.94437 11.0731C3.0399 10.4687 2.33495 9.60975 1.91867 8.60476C1.50238 7.59977 1.39347 6.4939 1.60568 5.427C1.8179 4.36011 2.34173 3.3801 3.11092 2.61091C3.8801 1.84172 4.86011 1.3179 5.92701 1.10568C6.9939 0.893462 8.09977 1.00238 9.10476 1.41866C10.1098 1.83494 10.9687 2.53989 11.5731 3.44436C12.1774 4.34883 12.5 5.4122 12.5 6.5C12.4983 7.95818 11.9184 9.35617 10.8873 10.3873C9.85617 11.4184 8.45819 11.9983 7 12ZM11 6.5C11 6.63261 10.9473 6.75979 10.8536 6.85355C10.7598 6.94732 10.6326 7 10.5 7H7C6.86739 7 6.74022 6.94732 6.64645 6.85355C6.55268 6.75979 6.5 6.63261 6.5 6.5V3C6.5 2.86739 6.55268 2.74021 6.64645 2.64645C6.74022 2.55268 6.86739 2.5 7 2.5C7.13261 2.5 7.25979 2.55268 7.35356 2.64645C7.44732 2.74021 7.5 2.86739 7.5 3V6H10.5C10.6326 6 10.7598 6.05268 10.8536 6.14645C10.9473 6.24021 11 6.36739 11 6.5Z"
                    fill="#9B88AA"
                  />
                </svg>
                {Term}
              </div>
            </section>
          );
        })}
        <section className="mt-3 font-helvetica text-[13px] text-[#83748E]">
          <table className="w-full">
            <tbody>
              <tr className="flex justify-between">
                <td className="text-left">
                  <p className="text-left text-[11px] font-bold">TOTAL LOAN</p>
                  <p className="text-left text-[14px]">
                    {currencyAbbreviation(TotalLoanAmount)}
                  </p>
                </td>
                <td className="flex justify-center pt-1.5">
                  <div className="h-[24px] w-[2px] border border-dashed border-[#3C435166]"></div>
                </td>
                <td>
                  <p className="text-left text-[11px] font-bold">DEPOSIT</p>
                  <p className="text-left text-[14px]">
                    {hasNegativeDeposit && "-"}
                    {currencyAbbreviation(
                      hasNegativeDeposit ? DepositAmount * -1 : DepositAmount,
                    )}
                  </p>
                </td>
                <td className="flex justify-center pt-1.5">
                  <div className="h-[24px] w-[2px] border border-dashed border-[#3C435166]"></div>
                </td>
                <td>
                  <p className="text-left text-[11px] font-bold">TOTAL LVR</p>
                  <p className="text-left text-[14px]">{LVR}%</p>
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      </section>
    </>
  );
}
